import { questionSchema } from '../pages/shared/QuestionnaireCreation/validation';

import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import {
    ReviewQuestionStatus,
    type ReviewQuestionStatusType,
} from '@/constants/questionnaire/Question';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { validationMessageKey } from '@/utils/validationMessage';

interface UseToggleQuestionStatusParams {
    updateQuestion: (question: ReviewQuestion) => Promise<unknown>;
    formQuestions: ReviewQuestion[];
    onSuccess: (updatedQuestions: ReviewQuestion[]) => void;
    toasterMessage?: TranslationKey;
}

export const useToggleQuestionStatus = ({
    updateQuestion,
    formQuestions,
    onSuccess,
}: UseToggleQuestionStatusParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const onToggleQuestionStatus = async (
        question: ReviewQuestion,
        newStatus: ReviewQuestionStatusType,
    ) => {
        if (!question.id) return;

        const updatedQuestion = { ...question, status: newStatus };
        const result = questionSchema.safeParse(updatedQuestion);

        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return;
        }

        try {
            await updateQuestion(result.data);

            const updatedQuestions = formQuestions.map((existingQuestion) =>
                existingQuestion.id === question.id
                    ? { ...existingQuestion, status: newStatus }
                    : existingQuestion,
            );

            onSuccess(updatedQuestions);

            showSuccessToast({
                message:
                    newStatus === ReviewQuestionStatus.ACTIVE
                        ? t('competenciesTab.competencyQuestions.messages.activated')
                        : t('competenciesTab.competencyQuestions.messages.deactivated'),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    return { onToggleQuestionStatus };
};
